import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import * as Alert from '@common/services/alert';
import Overview from '@common/components/overview';
import { AsyncTable, TableDropdown } from '@common/components/table';
import Dropdown from '@common/components/dropdown';
import { Button } from '@common/components/button';
import { Icon } from '@common/components/icon';
import { Select } from '@common/components/form/inputs/select';
import Container from '@common/components/container';
import Confirm from '@common/components/confirm-button';
import Bar from '@common/components/bar';
import { Toggle } from '@common/components/form/inputs/toggle';
import ShortcutForm from '../../forms/shortcut';
import AddFormShortcut from '../../forms/add-form-shortcut';
import AddDocumentShortcut from '../../forms/add-document-shortcut';

import { useAppSelector } from '@common/hooks';
import Api, { FailedRequest } from '@common/services/api';
import { removeQueryParam, transformUrl } from '@common/utils/url';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import { useEnabledComponents } from '@common/hooks/use-enabled-components';

import { EComponentTypes } from '@common/definitions';
import { ShortcutTypes } from '@modules/admin/definitions';
import { ORGANISATION_UPDATE_ORGANISATION } from '@modules/admin/actions';
import type { FetchShortcutsApiResponse, Shortcut, ShortcutResponse } from '@modules/social/types/objects';
import { Network, OrganisationFunction } from '@common/types/objects';
import formatEntityList from '@common/utils/format-entity-list';

import './styles.scss';

type ShowModalState = {
  document: boolean;
  form: boolean;
  link: boolean;
};

const getIconUrl = (path: string) => {
  try {
    return path.split('/shortcuts/')[1].split('.')[0];
  } catch (err) {
    return '';
  }
};

const parseResponse = (
  shortcut: Omit<Shortcut, 'data' | 'icon'>,
  {
    id, name, color, icon, web, networks, functions, type,
  }: ShortcutResponse,
): Shortcut => {
  return {
    ...shortcut,
    name,
    data: {
      id, web, networks, functions, type,
    },
    icon: {
      color, url: icon,
    },
  };
};

const OrganisationSettingsShortcuts = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams<{ shortcutId?: string }>();

  const networks = useAppSelector((state) => organisationSelector.networks(state));
  const functions = useAppSelector((state) => organisationSelector.functions(state));
  const organisation = useAppSelector((state) => state.organisation.selected);
  const orgId = organisation.id;

  const { hasHome, hasForms, hasDocuments } = useEnabledComponents({
    hasHome: EComponentTypes.HOME,
    hasForms: EComponentTypes.FORMS,
    hasDocuments: EComponentTypes.DOCUMENTS,
  });

  const [networkFilter, setNetworkFilter] = useState<Network[]>([]);
  const [functionFilter, setFunctionFilter] = useState<OrganisationFunction[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [showModal, setShowModal] = useState<ShowModalState>({
    document: false,
    link: false,
    form: false,
  });

  const editingShortcut = params.shortcutId && shortcuts.find(({ id }) => params.shortcutId === id);

  const onFetch = async () => {
    const { data } = await Api.get<FetchShortcutsApiResponse>(`/v1/organisations/${orgId}/menu-items`);
    setShortcuts(data);
    // Required to pass typescript check
    return {
      data,
      meta: { pagination: {} },
    };
  };

  const onDeleteShortcut = async (id: string) => {
    await Api.delete(`/v3/organisations/${orgId}/shortcuts/${id}`);
    setShortcuts((current) => current.filter((shortcut) => shortcut.id !== id));
  };

  const onUpdateShortcut = (id: string, response: ShortcutResponse) => {
    setShortcuts((current) => current.map((shortcut) => {
      if (shortcut.id === id) {
        return parseResponse(shortcut, response);
      }
      return shortcut;
    }));
  };

  const onAddShortcut = (response: ShortcutResponse) => {
    setShortcuts((current) => [
      ...current,
      parseResponse(response, response),
    ]);
    setOrder((current) => [...current, response.id]);
  };

  const handleChangeOrder = (sourceId: string, targetId: string) => {
    setOrder((current) => {
      const sourcePath = current.find((id) => id === sourceId);
      if (!sourcePath) return current;
      const targetIndex = current.findIndex((id) => id === targetId);
      const newOrder = current.filter((id) => id !== sourceId);
      newOrder.splice(targetIndex, 0, sourcePath);
      return newOrder;
    });
  };

  const handleSaveOrder = async () => {
    try {
      await Api.put(`/v1/organisations/${orgId}/menu-items/order`, { order });
      Alert.success(t('learning:saved_order'));
    } catch (e) {
      Alert.forStatus((e as FailedRequest).status_code, {
        warning: t('learning:warning_saving_order'),
        error: t('learning:error_saving_order'),
      });
      throw e;
    }
  };

  const handleToggleComponent = async (item: Shortcut, enabled: boolean) => {
    try {
      const payload = {
        add: enabled ? [item.id] : [],
        remove: enabled ? [] : [item.id],
      };
      await Api.put(`/v2/organisations/${orgId}/components`, payload);
      setShortcuts((current) => current.map((shortcut) => {
        if (shortcut.id === item.id) {
          return {
            ...shortcut,
            data: { ...shortcut.data, enabled },
          };
        }
        return shortcut;
      }));

      dispatch({
        type: ORGANISATION_UPDATE_ORGANISATION,
        organisation: {
          ...organisation,
          enabled_components: enabled ?
            [...organisation.enabled_components, item.id] :
            organisation.enabled_components.filter((component) => component !== item.id),
        },
      });

      Alert.success(t('organisation:forms_app_edit_success'));
    } catch (e) {
      Alert.forStatus((e as FailedRequest).status_code, {
        warning: t('organisation:forms_app_edit_warning'),
        error: t('organisation:forms_app_edit_error'),
      });
      throw e;
    }
  };

  // Filter shortcuts on network and function.
  // Order shortcuts on index
  const items = useMemo(() => {
    const networkIds = networkFilter.map(({ id }) => id);
    const functionsIds = functionFilter.map(({ id }) => id);
    return shortcuts
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
      .filter((shortcut) => {
        if (!networkIds.length && !functionsIds.length) return true;
        return (shortcut.data.functions && functionsIds.includes(shortcut.data.functions?.[0].id)) ||
          (shortcut.data.networks && networkIds.includes(shortcut.data.networks?.[0].id));
      });
  }, [shortcuts, networkFilter, functionFilter, order]);

  const columns = useMemo(() => {
    return [
      { size: 0 },
      {
        label: t('organisation:shortcuts_columns_name'),
        className: 'Table__Cell__Title',
        size: 'large'
      },
      {
        label: t('organisation:shortcuts_columns_link'),
        className: 'Table__Cell--clickable'
      },
      {
        label: t('core:communities'),
        className: 'Table__Cell__Title'
      },
      {
        label: t('core:function_groups'),
        className: 'Table__Cell__Title'
      }
    ];
  }, [t]);

  return (
    <Container name="SettingsFunctions">
      {editingShortcut && (
        <ShortcutForm
          show
          form={`shortcut-${editingShortcut.id}`}
          initialValues={{
            ...editingShortcut,
            web_url: removeQueryParam(editingShortcut.data.web?.url, 'token') || '',
            functions: editingShortcut?.data?.functions || [],
            networks: editingShortcut?.data?.networks || [],
            icon: {
              ...editingShortcut.icon,
              url: getIconUrl(editingShortcut.icon.url),
            },
          }}
          onUpdate={onUpdateShortcut}
          onClose={() => history.push('/admin/settings/shortcuts')}
        />
      )}
      <ShortcutForm
        show={showModal.link}
        onAdd={onAddShortcut}
        onClose={() => setShowModal((current) => ({ ...current, link: false }))}
      />
      <AddFormShortcut
        show={showModal.form}
        onAdd={onAddShortcut}
        onClose={() => setShowModal((current) => ({ ...current, form: false }))}
      />
      <AddDocumentShortcut
        show={showModal.document}
        onAdd={onAddShortcut}
        onClose={() => setShowModal((current) => ({ ...current, document: false }))}
      />
      <Container.Content>
        <Bar>
          <h2>
            {hasHome ?
              t('organisation:settings_index_shortcuts_apps_title') :
              t('organisation:settings_index_shortcuts_title')}
          </h2>
          <Select
            multi
            simplifyMultiValue
            onChange={(v) => setNetworkFilter(v as Network[])}
            value={networkFilter}
            options={networks}
            labelKey="name"
            valueKey="id"
            placeholder={(
              <>
                <Icon type="public" />
                {t('organisation:settings_all_communities_filter')}
              </>
            )}
          />
          <Select
            multi
            simplifyMultiValue
            onChange={(v) => setFunctionFilter(v as OrganisationFunction[])}
            value={functionFilter}
            options={functions}
            labelKey="name"
            valueKey="id"
            placeholder={(
              <>
                <Icon type="assignment" />
                {t('organisation:settings_all_functions_filter')}
              </>
            )}
          />
          {hasHome && (hasForms || hasDocuments) ? (
            <Dropdown
              alignRight
              toggle={(
                <Button type="primary" iconRight="add" size="large">
                  <Trans i18nKey="organisation:settings_shortcuts_add_button" />
                </Button>
              )}
            >
              <TableDropdown.Item onClick={() => setShowModal((current) => ({ ...current, link: true }))}>
                <div role="button">
                  <Icon type="insert_link" />
                  <Trans i18nKey="organisation:settings_shortcuts_add_external_link" />
                </div>
              </TableDropdown.Item>
              {hasForms && (
                <TableDropdown.Item onClick={() => setShowModal((current) => ({ ...current, form: true }))}>
                  <div role="button">
                    <Icon type="assignment" />
                    <Trans i18nKey="organisation:settings_shortcuts_add_form" />
                  </div>
                </TableDropdown.Item>
              )}
              {hasDocuments && (
                <TableDropdown.Item onClick={() => setShowModal((current) => ({ ...current, document: true }))}>
                  <div role="button">
                    <Icon type="document" />
                    <Trans i18nKey="organisation:settings_shortcuts_add_document" />
                  </div>
                </TableDropdown.Item>
              )}
            </Dropdown>
          ) : (
            <Button
              type="primary"
              iconRight="add"
              size="large"
              onClick={() => setShowModal((current) => ({ ...current, link: true }))}
            >
              <Trans i18nKey="organisation:settings_shortcuts_add_button" />
            </Button>
          )}
        </Bar>
        <Overview>
          <Overview.Content>
            <AsyncTable
              columns={columns}
              items={items}
              disableInitialFetch={shortcuts.length > 0}
              data={{
                useCursor: undefined,
                onFetch,
                onPostFetch: ({ data }: FetchShortcutsApiResponse) => setOrder(data.map((item) => item.id)),
              }}
              renderRow={({ item }) => {
                const title = item.type === ShortcutTypes.EXTERNAL_LINK ?
                  <div
                    onClick={() => history.push(`/admin/settings/shortcuts/${item.id}`)}
                    className="Shortcut__Title"
                    role="link"
                  >
                    {item.name}
                  </div> :
                  <span>{item.name}</span>;
                return [
                  <div className="Shortcut__Icon" style={{ backgroundColor: item.icon.color }}>
                    <img src={item.icon.url} alt={item.name} />
                  </div>,
                  title,
                  item.data?.web && (
                    <a href={transformUrl(item.data.web.url)} target="_blank" rel="noreferrer">
                      {removeQueryParam(item.data.web.url, 'token')}
                    </a>
                  ),
                  <span>
                    {formatEntityList(item.data.networks?.map((n) => n.name)) || '-'}
                  </span>,
                  <span>
                    {formatEntityList(item.data.functions?.map((n) => n.name)) || '-'}
                  </span>
                ];
              }}
              onChangeOrder={handleChangeOrder}
              onDrop={handleSaveOrder}
              placeholder={(
                <div className="Table__Placeholder">
                  <Trans i18nKey="organisation:settings_shortcuts_empty" />
                </div>
              )}
              ActionComponent={({ item }) => {
                if (item.type === ShortcutTypes.COMPONENT) {
                  return (
                    <Toggle
                      value={!!item.data.enabled}
                      onChange={(enabled) => handleToggleComponent(item, enabled)}
                    />
                  );
                }
                return (
                  <TableDropdown id="function-actions-dropdown">
                    {item.data.type === ShortcutTypes.EXTERNAL_LINK && (
                      <TableDropdown.Item onClick={() => history.push(`/admin/settings/shortcuts/${item.id}`)}>
                        <Trans i18nKey="organisation:shortcut_actions_edit_button" />
                      </TableDropdown.Item>
                    )}
                    <Confirm
                      danger
                      title={t('organisation:shortcut_actions_delete_title')}
                      onConfirm={() => onDeleteShortcut(item.id)}
                    >
                      <TableDropdown.Item danger>
                        <Trans i18nKey="organisation:shortcut_actions_delete_button" />
                      </TableDropdown.Item>
                    </Confirm>
                  </TableDropdown>
                );
              }}
            />
          </Overview.Content>
        </Overview>
      </Container.Content>
    </Container>
  );
};

export default OrganisationSettingsShortcuts;
